import {
  ArrowRight,
  Bell,
  Boxes,
  Check,
  ChevronDown,
  CircleDollarSign,
  FileClock,
  Gauge,
  Layers3,
  Lightbulb,
  ListChecks,
  LockKeyhole,
  Menu,
  PackageCheck,
  Plus,
  ReceiptText,
  Search,
  Settings,
  ShoppingBag,
  Sparkles,
  TrendingUp,
  Upload,
  WalletCards,
  X,
} from "lucide-react";
import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import logoUrl from "../../../packages/ui/brand/logo-lockup.svg";
import {
  ApiRequestError,
  apiRequest,
  type DashboardPayload,
  type SessionUser,
} from "./api";

const marketingLinks = [
  { href: "/product", label: "Product" },
  { href: "/pricing", label: "Pricing" },
  { href: "/security", label: "Security" },
];

const appNav = [
  { href: "/dashboard", label: "Overview", icon: Gauge },
  { href: "/inventory", label: "Inventory", icon: Boxes },
  { href: "/listings", label: "Listings", icon: ShoppingBag },
  { href: "/sales", label: "Sales", icon: TrendingUp },
  { href: "/expenses", label: "Expenses", icon: ReceiptText },
  { href: "/intelligence", label: "Intelligence", icon: Lightbulb },
  { href: "/projects", label: "Projects", icon: FileClock },
];

const demoDashboard: DashboardPayload = {
  generatedAt: Date.now(),
  baseCurrency: "GBP",
  performance: {
    current: {
      sales: {
        count: 38,
        revenueBaseAmountMinor: 1245800,
        averageSaleBaseAmountMinor: 32784,
      },
      operatingCosts: { totalBaseAmountMinor: 118400 },
      netProfitBaseAmountMinor: 328600,
    },
    change: {
      revenue: { changePercent: 18.4 },
      netProfit: { changePercent: 12.8 },
      salesCount: { changePercent: 8.6 },
    },
  },
  inventory: {
    itemCount: 71,
    holdingCount: 126,
    totalCostBaseAmountMinor: 816200,
    marketBaseAmountMinor: 1184600,
    potentialProfitBaseAmountMinor: 368400,
    roiPercent: 45.1,
    valuationCoveragePercent: 86,
    listings: { activeCount: 84, draftCount: 7, expiredCount: 3 },
    ageing: { overThresholdHoldingCount: 9 },
  },
  trend: [
    19000, 26000, 18000, 34000, 41000, 29000, 47000, 52000, 44000, 61000, 57000,
    68000,
  ].map((value, index) => ({
    bucketStart: Date.now() - (11 - index) * 24 * 60 * 60 * 1000,
    revenueBaseAmountMinor: value * 2,
    netProfitBaseAmountMinor: value,
  })),
  recentSales: {
    items: [
      {
        id: "sample-1",
        item: { name: "Omega Seamaster 300" },
        marketplaceName: "eBay",
        soldAt: Date.now() - 45 * 60 * 1000,
        status: "completed",
        revenueBaseAmountMinor: 286000,
        netProfitBaseAmountMinor: 62400,
      },
      {
        id: "sample-2",
        item: { name: "Nike SB Dunk Low" },
        marketplaceName: "StockX",
        soldAt: Date.now() - 4 * 60 * 60 * 1000,
        status: "completed",
        revenueBaseAmountMinor: 26400,
        netProfitBaseAmountMinor: 8700,
      },
      {
        id: "sample-3",
        item: { name: "Leica M6 Classic" },
        marketplaceName: "Shopify",
        soldAt: Date.now() - 28 * 60 * 60 * 1000,
        status: "completed",
        revenueBaseAmountMinor: 248500,
        netProfitBaseAmountMinor: 48500,
      },
    ],
  },
  attention: [
    { code: "inventory_over_90_days", severity: "info", count: 9 },
    { code: "draft_listings", severity: "info", count: 7 },
    { code: "expired_listings", severity: "warning", count: 3 },
  ],
};

function setPageMetadata(path: string) {
  const titles: Record<string, string> = {
    "/": "MarginFlip — Your margin, made actionable",
    "/product": "Product — MarginFlip",
    "/pricing": "Pricing — MarginFlip",
    "/security": "Security — MarginFlip",
    "/sign-in": "Sign in — MarginFlip",
    "/create-account": "Create account — MarginFlip",
    "/dashboard": "Dashboard — MarginFlip",
  };
  document.title = titles[path] ?? "MarginFlip";
  const privateRoute = [
    "/sign-in",
    "/create-account",
    "/dashboard",
    "/inventory",
    "/listings",
    "/sales",
    "/expenses",
    "/intelligence",
    "/projects",
  ].includes(path);
  let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (!robots) {
    robots = document.createElement("meta");
    robots.name = "robots";
    document.head.append(robots);
  }
  robots.content = privateRoute ? "noindex, nofollow" : "index, follow";
  let canonical = document.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.append(canonical);
  }
  canonical.href = `https://marginflip.com${path}`;
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <a className={`brand ${compact ? "brand-compact" : ""}`} href="/">
      <img src={logoUrl} alt="MarginFlip" />
      <span className="beta-pill">Working-name beta</span>
    </a>
  );
}

function MarketingHeader() {
  const [open, setOpen] = useState(false);
  return (
    <header className="marketing-header">
      <div className="site-container header-inner">
        <Brand />
        <nav
          className={`marketing-nav ${open ? "is-open" : ""}`}
          aria-label="Main navigation"
        >
          {marketingLinks.map((link) => (
            <a key={link.href} href={link.href}>
              {link.label}
            </a>
          ))}
          <a className="mobile-only-link" href="/sign-in">
            Sign in
          </a>
          <a
            className="button button-primary mobile-only-link"
            href="/dashboard"
          >
            View dashboard
          </a>
        </nav>
        <div className="header-actions">
          <a className="text-link" href="/sign-in">
            Sign in
          </a>
          <a className="button button-primary button-small" href="/dashboard">
            View dashboard <ArrowRight size={15} />
          </a>
        </div>
        <button
          className="menu-button"
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="site-container footer-grid">
        <div>
          <Brand compact />
          <p>Know your true margin. Know your next move.</p>
        </div>
        <div className="footer-links">
          <a href="/product">Product</a>
          <a href="/pricing">Pricing</a>
          <a href="/security">Security</a>
          <a href="/sign-in">Sign in</a>
        </div>
        <p className="footer-note">
          MarginFlip is the current working product name. Private beta · 2026
        </p>
      </div>
    </footer>
  );
}

function MiniDashboard() {
  return (
    <div className="mini-dashboard" aria-label="MarginFlip dashboard preview">
      <div className="mini-topbar">
        <div className="mini-mark">M</div>
        <div className="mini-search">
          <Search size={13} /> Search inventory
        </div>
        <div className="mini-avatar">CS</div>
      </div>
      <div className="mini-body">
        <aside className="mini-sidebar">
          {[Gauge, Boxes, ShoppingBag, TrendingUp, Lightbulb].map(
            (Icon, index) => (
              <span className={index === 0 ? "active" : ""} key={index}>
                <Icon size={14} />
              </span>
            ),
          )}
        </aside>
        <div className="mini-content">
          <div className="mini-heading">
            <div>
              <small>Good morning</small>
              <strong>Your margin at a glance</strong>
            </div>
            <span>Sample data</span>
          </div>
          <div className="mini-kpis">
            <div>
              <small>Net profit</small>
              <strong>£3,286</strong>
              <em>↑ 12.8%</em>
            </div>
            <div>
              <small>Revenue</small>
              <strong>£12,458</strong>
              <em>↑ 18.4%</em>
            </div>
            <div>
              <small>Stock value</small>
              <strong>£11,846</strong>
              <em>126 units</em>
            </div>
          </div>
          <div className="mini-panels">
            <div className="mini-chart">
              <div>
                <strong>Profit trend</strong>
                <small>Last 12 days</small>
              </div>
              <div className="mini-bars">
                {[28, 42, 35, 51, 64, 47, 72, 66, 78, 61, 82, 92].map(
                  (height, index) => (
                    <i key={index} style={{ height: `${height}%` }} />
                  ),
                )}
              </div>
            </div>
            <div className="mini-actions">
              <strong>Needs attention</strong>
              <p>
                <span className="dot amber" /> 9 items ageing
              </p>
              <p>
                <span className="dot purple" /> 7 draft listings
              </p>
              <p>
                <span className="dot red" /> 3 listings expired
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const capabilityCards = [
  {
    icon: Boxes,
    eyebrow: "Inventory control",
    title: "Know exactly what you hold.",
    body: "Track unique pieces or repeat stock, acquisition cost, condition, valuations and the capital tied up in every item.",
  },
  {
    icon: CircleDollarSign,
    eyebrow: "True margin",
    title: "See what you actually earned.",
    body: "Keep fees, shipping, tax, returns and operating costs in the calculation so revenue never masquerades as profit.",
  },
  {
    icon: Sparkles,
    eyebrow: "Decision intelligence",
    title: "Turn the numbers into a next move.",
    body: "Surface ageing stock, valuation gaps, listing work and market signals without losing the evidence behind the answer.",
  },
];

function MarketingPage({ productFocus = false }: { productFocus?: boolean }) {
  return (
    <div className="marketing-page">
      <MarketingHeader />
      <main>
        <section className={`hero ${productFocus ? "hero-product" : ""}`}>
          <div className="hero-glow" />
          <div className="site-container hero-grid">
            <div className="hero-copy">
              <p className="eyebrow">
                <span /> Commerce operations for modern sellers
              </p>
              <h1>
                {productFocus
                  ? "One operating view for every item and every margin."
                  : "Your margin, made actionable."}
              </h1>
              <p className="hero-lede">
                Track what you own, what it cost, where it is listed and what it
                really earned—then see the next move that deserves your
                attention.
              </p>
              <div className="hero-actions">
                <a
                  className="button button-primary button-large"
                  href="/dashboard"
                >
                  View the dashboard <ArrowRight size={18} />
                </a>
                <a className="button button-quiet button-large" href="/pricing">
                  See the access boundary
                </a>
              </div>
              <p className="hero-footnote">
                <Check size={15} /> Free users can create, finish and export.
                Paid persistence comes later.
              </p>
            </div>
            <div className="hero-visual">
              <MiniDashboard />
            </div>
          </div>
        </section>

        <section className="trust-strip">
          <div className="site-container trust-grid">
            <span>Built for</span>
            <strong>Resellers</strong>
            <strong>Collectibles</strong>
            <strong>E-commerce</strong>
            <strong>Independent stores</strong>
          </div>
        </section>

        <section className="section site-container" id="product">
          <div className="section-heading">
            <p className="eyebrow">The operating layer</p>
            <h2>Less spreadsheet archaeology. More confident decisions.</h2>
            <p>
              MarginFlip joins the commercial facts that normally live in
              separate tabs and tools.
            </p>
          </div>
          <div className="capability-grid">
            {capabilityCards.map(({ icon: Icon, eyebrow, title, body }) => (
              <article className="capability-card" key={title}>
                <span className="icon-tile">
                  <Icon size={22} />
                </span>
                <small>{eyebrow}</small>
                <h3>{title}</h3>
                <p>{body}</p>
                <a href="/dashboard">
                  See it in the dashboard <ArrowRight size={15} />
                </a>
              </article>
            ))}
          </div>
        </section>

        <section className="section workflow-section">
          <div className="site-container workflow-grid">
            <div>
              <p className="eyebrow">A clearer operating rhythm</p>
              <h2>From acquisition to answer.</h2>
              <p className="section-lede">
                Capture the facts once. MarginFlip keeps the financial story
                coherent as an item moves through your business.
              </p>
            </div>
            <ol className="workflow-list">
              <li>
                <span>01</span>
                <div>
                  <strong>Record the buy</strong>
                  <p>
                    Cost, source, variant, condition and supporting evidence.
                  </p>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <strong>Operate the listing</strong>
                  <p>
                    Keep channels, listing state and stock identity connected.
                  </p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <strong>Close the true margin</strong>
                  <p>
                    Revenue minus every attributable cost, including returns.
                  </p>
                </div>
              </li>
              <li>
                <span>04</span>
                <div>
                  <strong>Act on the signal</strong>
                  <p>
                    See ageing capital, missing valuations and the next task.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        <section className="section site-container">
          <div className="cta-panel">
            <div>
              <p className="eyebrow">Private beta v1</p>
              <h2>Start with one clear view of your margin.</h2>
              <p>
                The dashboard preview is live now. Account storage, Google
                sign-in and billing stay behind their approval gates.
              </p>
            </div>
            <a className="button button-inverse button-large" href="/dashboard">
              Open dashboard <ArrowRight size={18} />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function PricingPage() {
  return (
    <div className="marketing-page">
      <MarketingHeader />
      <main className="simple-page">
        <section className="site-container simple-hero">
          <p className="eyebrow">Persistence, not a worse product</p>
          <h1>Finish now for free. Save and continue when paid.</h1>
          <p>
            Every user gets the same core workspace. The clean boundary is
            whether MarginFlip stores ongoing projects in your account.
          </p>
        </section>
        <section className="site-container pricing-grid">
          <article className="price-card">
            <p className="plan-label">Free</p>
            <h2>Create, finish and export now.</h2>
            <p className="price-note">No permanent hosted project storage</p>
            <ul>
              <li>
                <Check /> Create and edit a project
              </li>
              <li>
                <Check /> Process within free limits
              </li>
              <li>
                <Check /> Preview and export results
              </li>
              <li>
                <Check /> Download a local project file where supported
              </li>
            </ul>
            <a className="button button-quiet button-large" href="/dashboard">
              View dashboard
            </a>
          </article>
          <article className="price-card featured">
            <span className="recommended">Continuity</span>
            <p className="plan-label">Paid</p>
            <h2>Save, return and manage ongoing work.</h2>
            <p className="price-note">
              Final public pricing is not yet approved
            </p>
            <ul>
              <li>
                <Check /> Save to Projects
              </li>
              <li>
                <Check /> Automatic cloud persistence
              </li>
              <li>
                <Check /> Reopen and continue across sessions
              </li>
              <li>
                <Check /> Project history and larger allowances
              </li>
            </ul>
            <a className="button button-primary button-large" href="/dashboard">
              Preview the product
            </a>
          </article>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function SecurityPage() {
  const points = [
    [
      LockKeyhole,
      "Private by default",
      "Account data is owner-scoped. Private media and project bodies live behind authenticated access.",
    ],
    [
      Layers3,
      "Fail-closed providers",
      "Email delivery, Google sign-in and billing stay disabled until their credentials and provider setup are explicitly approved.",
    ],
    [
      ListChecks,
      "Auditable actions",
      "Authentication and material account actions are coupled to privacy-minimal audit records.",
    ],
  ] as const;
  return (
    <div className="marketing-page">
      <MarketingHeader />
      <main className="simple-page">
        <section className="site-container simple-hero">
          <p className="eyebrow">Security foundation</p>
          <h1>Useful software without casual trust.</h1>
          <p>
            MarginFlip’s v1 keeps provider integrations dark by default and
            makes ownership boundaries part of the data model.
          </p>
        </section>
        <section className="site-container security-grid">
          {points.map(([Icon, title, body]) => (
            <article key={title}>
              <span className="icon-tile">
                <Icon />
              </span>
              <h2>{title}</h2>
              <p>{body}</p>
            </article>
          ))}
        </section>
      </main>
      <Footer />
    </div>
  );
}

function AuthPage({ mode }: { mode: "sign-in" | "create-account" }) {
  const creating = mode === "create-account";
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const values = new FormData(event.currentTarget);
    const body = creating
      ? {
          displayName: values.get("displayName"),
          email: values.get("email"),
          password: values.get("password"),
          baseCurrency: values.get("baseCurrency"),
          sellerType: values.get("sellerType"),
        }
      : { email: values.get("email"), password: values.get("password") };
    try {
      await apiRequest(creating ? "/auth/sign-up" : "/auth/sign-in", {
        method: "POST",
        body: JSON.stringify(body),
      });
      window.location.assign("/dashboard");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Unable to continue.",
      );
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-page">
      <a className="auth-brand" href="/">
        <img src={logoUrl} alt="MarginFlip" />
      </a>
      <div className="auth-grid">
        <section className="auth-story">
          <p className="eyebrow">Margin clarity starts here</p>
          <h1>
            {creating
              ? "Build the operating view your stock deserves."
              : "Welcome back to your margin."}
          </h1>
          <p>
            Keep inventory, listings, true profit and the next action in one
            calm place.
          </p>
          <div className="auth-proof">
            <MiniDashboard />
          </div>
        </section>
        <main className="auth-card">
          <p className="auth-kicker">
            {creating ? "Create your account" : "Sign in"}
          </p>
          <h2>
            {creating
              ? "Start with the full workspace."
              : "Continue where you left off."}
          </h2>
          <p className="auth-subtitle">
            {creating
              ? "Free means finish and export now. Paid persistence will be a separate upgrade."
              : "Production account sign-in is not active yet."}
          </p>
          <button
            className="provider-button"
            type="button"
            disabled
            title="Requires approved Google OAuth configuration"
          >
            <span className="google-g">G</span> Continue with Google{" "}
            <span>Approval required</span>
          </button>
          <div className="divider">
            <span>or use email</span>
          </div>
          <form onSubmit={submit}>
            {creating && (
              <label>
                Display name
                <input
                  name="displayName"
                  autoComplete="name"
                  required
                  maxLength={100}
                  placeholder="Chase"
                />
              </label>
            )}
            <label>
              Email address
              <input
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
              />
            </label>
            <label>
              Password
              <input
                name="password"
                type="password"
                autoComplete={creating ? "new-password" : "current-password"}
                minLength={10}
                required
                placeholder={
                  creating ? "At least 10 characters" : "Your password"
                }
              />
            </label>
            {creating && (
              <div className="form-row">
                <label>
                  Base currency
                  <select name="baseCurrency" defaultValue="GBP">
                    <option>GBP</option>
                    <option>USD</option>
                    <option>EUR</option>
                    <option>CAD</option>
                    <option>AUD</option>
                  </select>
                </label>
                <label>
                  You sell as
                  <select name="sellerType" defaultValue="both">
                    <option value="both">Seller & reseller</option>
                    <option value="reseller">Reseller</option>
                    <option value="dropshipper">E-commerce seller</option>
                  </select>
                </label>
              </div>
            )}
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <button
              className="button button-primary button-large auth-submit"
              disabled={submitting}
              type="submit"
            >
              {submitting
                ? "Working…"
                : creating
                  ? "Check account availability"
                  : "Check sign-in availability"}{" "}
              {!submitting && <ArrowRight size={18} />}
            </button>
          </form>
          <p className="auth-switch">
            {creating ? "Already have an account?" : "New to MarginFlip?"}{" "}
            <a href={creating ? "/sign-in" : "/create-account"}>
              {creating ? "Sign in" : "Create account"}
            </a>
          </p>
          <p className="auth-boundary">
            Google sign-in and outbound verification email are not active in
            this v1. We will request approval before enabling either provider.
          </p>
        </main>
      </div>
    </div>
  );
}

function Money({ value, currency }: { value: number; currency: string }) {
  return (
    <>
      {new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(value / 100)}
    </>
  );
}

function ChangeBadge({ value }: { value: number | null | undefined }) {
  if (value === null || value === undefined)
    return <span className="change neutral">No comparison</span>;
  const positive = value >= 0;
  return (
    <span className={`change ${positive ? "positive" : "negative"}`}>
      {positive ? "↑" : "↓"} {Math.abs(value).toFixed(1)}%
    </span>
  );
}

function KpiCard({
  label,
  value,
  detail,
  icon,
}: {
  label: string;
  value: ReactNode;
  detail: ReactNode;
  icon: ReactNode;
}) {
  return (
    <article className="kpi-card">
      <div className="kpi-top">
        <span>{label}</span>
        <i>{icon}</i>
      </div>
      <strong className="tabular">{value}</strong>
      <div className="kpi-detail">{detail}</div>
    </article>
  );
}

function DashboardShell({
  children,
  activePath,
  user,
  demo,
}: {
  children: ReactNode;
  activePath: string;
  user: SessionUser | null;
  demo: boolean;
}) {
  const [mobileNav, setMobileNav] = useState(false);
  async function signOut() {
    try {
      await apiRequest("/auth/sign-out", { method: "POST" });
    } finally {
      window.location.assign("/");
    }
  }
  return (
    <div className="app-shell">
      <aside className={`app-sidebar ${mobileNav ? "open" : ""}`}>
        <div className="sidebar-brand">
          <Brand compact />
          <button
            type="button"
            onClick={() => setMobileNav(false)}
            aria-label="Close navigation"
          >
            <X />
          </button>
        </div>
        <nav aria-label="Dashboard navigation">
          <p>Workspace</p>
          {appNav.map(({ href, label, icon: Icon }) => (
            <a
              href={href}
              key={href}
              aria-current={activePath === href ? "page" : undefined}
            >
              <Icon size={18} />
              {label}
              {href !== "/dashboard" && <span className="api-ready">API</span>}
            </a>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <a href="/security">
            <LockKeyhole size={17} /> Security
          </a>
          <button type="button">
            <Settings size={17} /> Settings
          </button>
          <div className="sidebar-account">
            <span>{user?.email.slice(0, 2).toUpperCase() ?? "MF"}</span>
            <div>
              <strong>{user?.email ?? "Product preview"}</strong>
              <small>
                {demo ? "Sample data" : `${user?.tier ?? "free"} account`}
              </small>
            </div>
            {user && (
              <button type="button" onClick={signOut} aria-label="Sign out">
                <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </aside>
      <div className="app-main">
        <header className="app-topbar">
          <button
            className="app-menu"
            type="button"
            onClick={() => setMobileNav(true)}
            aria-label="Open navigation"
          >
            <Menu />
          </button>
          <div className="app-search">
            <Search size={17} />
            <span>Search inventory, listings and sales</span>
            <kbd>⌘ K</kbd>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Notifications"
          >
            <Bell size={19} />
            <span />
          </button>
          {!user && (
            <a className="button button-primary button-small" href="/sign-in">
              Sign in
            </a>
          )}
        </header>
        {children}
      </div>
    </div>
  );
}

function DashboardPage() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [data, setData] = useState<DashboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const me = await apiRequest<{ user: SessionUser }>("/auth/me");
        const dashboard = await apiRequest<DashboardPayload>("/dashboard");
        if (active) {
          setUser(me.user);
          setData(dashboard);
        }
      } catch (caught) {
        if (!active) return;
        if (caught instanceof ApiRequestError && caught.status === 401)
          setData(demoDashboard);
        else {
          setData(demoDashboard);
          setError(
            "Live account data is temporarily unavailable. Showing the labelled product preview instead.",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading || !data)
    return (
      <DashboardShell activePath="/dashboard" user={null} demo>
        <main className="dashboard-content">
          <div className="dashboard-loading">
            <span />
            <p>Preparing your operating view…</p>
          </div>
        </main>
      </DashboardShell>
    );
  const demo = user === null;
  const currency = data.baseCurrency;
  const values = data.trend.slice(-14);
  const maxTrend = Math.max(
    1,
    ...values.map((point) => Math.abs(point.netProfitBaseAmountMinor)),
  );
  const coverage = data.inventory.valuationCoveragePercent ?? 0;

  return (
    <DashboardShell activePath="/dashboard" user={user} demo={demo}>
      <main className="dashboard-content">
        <div className="dashboard-heading">
          <div>
            <p>{demo ? "Product preview" : "Good morning"}</p>
            <h1>Your margin at a glance</h1>
            <span>
              {demo
                ? "Sample data — account activation follows production auth approval."
                : "A complete 30-day operating view from your account."}
            </span>
          </div>
          <div className="dashboard-actions">
            <button className="button button-quiet" type="button">
              <Upload size={17} /> Import
            </button>
            <button className="button button-primary" type="button">
              <Plus size={17} /> Add inventory
            </button>
          </div>
        </div>
        {error && <div className="status-banner warning">{error}</div>}
        {demo && (
          <div className="status-banner preview">
            <Sparkles size={17} />
            <span>
              <strong>This is a live, interactive product preview.</strong> The
              numbers are sample data; account data follows after production
              authentication is approved.
            </span>
            <a href="/pricing">
              See the access plan <ArrowRight size={15} />
            </a>
          </div>
        )}
        {user && !user.emailVerified && (
          <div className="status-banner warning">
            <Bell size={17} />
            <span>
              <strong>Email verification delivery is not active yet.</strong>{" "}
              Read-only account views work, while verified-only changes remain
              locked until the provider is approved.
            </span>
          </div>
        )}

        <section className="kpi-grid" aria-label="Key performance indicators">
          <KpiCard
            label="Net profit"
            icon={<TrendingUp />}
            value={
              <Money
                value={data.performance.current.netProfitBaseAmountMinor}
                currency={currency}
              />
            }
            detail={
              <>
                <ChangeBadge
                  value={data.performance.change?.netProfit.changePercent}
                />{" "}
                <span>vs previous period</span>
              </>
            }
          />
          <KpiCard
            label="Revenue"
            icon={<WalletCards />}
            value={
              <Money
                value={data.performance.current.sales.revenueBaseAmountMinor}
                currency={currency}
              />
            }
            detail={
              <>
                <ChangeBadge
                  value={data.performance.change?.revenue.changePercent}
                />{" "}
                <span>
                  {data.performance.current.sales.count} completed sales
                </span>
              </>
            }
          />
          <KpiCard
            label="Inventory value"
            icon={<Boxes />}
            value={
              <Money
                value={data.inventory.marketBaseAmountMinor}
                currency={currency}
              />
            }
            detail={
              <>
                <span className="change positive">
                  {data.inventory.roiPercent?.toFixed(1) ?? "—"}% ROI
                </span>
                <span>{data.inventory.holdingCount} units held</span>
              </>
            }
          />
          <KpiCard
            label="Potential margin"
            icon={<CircleDollarSign />}
            value={
              <Money
                value={data.inventory.potentialProfitBaseAmountMinor}
                currency={currency}
              />
            }
            detail={
              <>
                <span className="change neutral">
                  {coverage.toFixed(0)}% valued
                </span>
                <span>{data.inventory.itemCount} item lines</span>
              </>
            }
          />
        </section>

        <section className="dashboard-grid-primary">
          <article className="panel trend-panel">
            <div className="panel-heading">
              <div>
                <p>Net profit trend</p>
                <span>Daily performance, last {values.length} points</span>
              </div>
              <button type="button">
                Last 30 days <ChevronDown size={15} />
              </button>
            </div>
            <div
              className="trend-chart"
              role="img"
              aria-label="Daily net profit bar chart"
            >
              <div className="chart-scale">
                <span>
                  <Money value={maxTrend} currency={currency} />
                </span>
                <span>
                  <Money value={Math.round(maxTrend / 2)} currency={currency} />
                </span>
                <span>£0</span>
              </div>
              <div className="chart-bars">
                {values.map((point) => (
                  <div className="bar-slot" key={point.bucketStart}>
                    <i
                      className={
                        point.netProfitBaseAmountMinor < 0 ? "loss" : ""
                      }
                      style={{
                        height: `${Math.max(4, (Math.abs(point.netProfitBaseAmountMinor) / maxTrend) * 100)}%`,
                      }}
                      title={`${new Date(point.bucketStart).toLocaleDateString()}: ${point.netProfitBaseAmountMinor / 100}`}
                    />
                    <small>
                      {new Date(point.bucketStart).toLocaleDateString("en-GB", {
                        day: "numeric",
                      })}
                    </small>
                  </div>
                ))}
              </div>
            </div>
            <div className="chart-legend">
              <span>
                <i className="legend-profit" /> Net profit
              </span>
              <span>All amounts use the account’s captured base currency</span>
            </div>
          </article>
          <article className="panel attention-panel">
            <div className="panel-heading">
              <div>
                <p>Needs attention</p>
                <span>Ordered by operating impact</span>
              </div>
              <span className="attention-count">{data.attention.length}</span>
            </div>
            {data.attention.length === 0 ? (
              <div className="empty-attention">
                <PackageCheck />
                <strong>Nothing urgent</strong>
                <p>
                  MarginFlip will surface valuation, listing and ageing work
                  here.
                </p>
              </div>
            ) : (
              <div className="attention-list">
                {data.attention.slice(0, 4).map((item) => (
                  <div key={item.code}>
                    <span className={`attention-icon ${item.severity}`}>
                      <Bell size={16} />
                    </span>
                    <div>
                      <strong>{item.code.replaceAll("_", " ")}</strong>
                      <small>
                        {item.count} {item.count === 1 ? "item" : "items"} need
                        review
                      </small>
                    </div>
                    <ArrowRight size={16} />
                  </div>
                ))}
              </div>
            )}
            <a className="panel-link" href="/inventory">
              Open inventory review <ArrowRight size={15} />
            </a>
          </article>
        </section>

        <section className="dashboard-grid-secondary">
          <article className="panel recent-panel">
            <div className="panel-heading">
              <div>
                <p>Recent sales</p>
                <span>Revenue and net margin stay side by side</span>
              </div>
              <a href="/sales">
                View all <ArrowRight size={15} />
              </a>
            </div>
            {data.recentSales.items.length === 0 ? (
              <div className="table-empty">
                <ShoppingBag />
                <strong>No completed sales yet</strong>
                <p>
                  Your first recorded sale will appear here with its true
                  margin.
                </p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Channel</th>
                      <th>Sold</th>
                      <th>Revenue</th>
                      <th>Net profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentSales.items.slice(0, 5).map((sale) => (
                      <tr key={sale.id}>
                        <td>
                          <span className="item-thumb">
                            {sale.item.name?.slice(0, 1) ?? "I"}
                          </span>
                          <strong>{sale.item.name ?? "Unlinked item"}</strong>
                        </td>
                        <td>{sale.marketplaceName ?? "Direct"}</td>
                        <td>
                          {new Date(sale.soldAt).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                          })}
                        </td>
                        <td className="tabular">
                          <Money
                            value={sale.revenueBaseAmountMinor}
                            currency={currency}
                          />
                        </td>
                        <td
                          className={`tabular ${sale.netProfitBaseAmountMinor >= 0 ? "profit-text" : "loss-text"}`}
                        >
                          <Money
                            value={sale.netProfitBaseAmountMinor}
                            currency={currency}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </article>
          <article className="panel capital-panel">
            <div className="panel-heading">
              <div>
                <p>Inventory capital</p>
                <span>Current unsold holdings</span>
              </div>
              <Boxes size={19} />
            </div>
            <div className="capital-total">
              <small>Total cost</small>
              <strong>
                <Money
                  value={data.inventory.totalCostBaseAmountMinor}
                  currency={currency}
                />
              </strong>
            </div>
            <div className="progress-row">
              <span>
                <i style={{ width: `${Math.min(100, coverage)}%` }} />
              </span>
              <div>
                <strong>{coverage.toFixed(0)}%</strong>
                <small>valuation coverage</small>
              </div>
            </div>
            <dl>
              <div>
                <dt>Active listings</dt>
                <dd>{data.inventory.listings.activeCount}</dd>
              </div>
              <div>
                <dt>Draft listings</dt>
                <dd>{data.inventory.listings.draftCount}</dd>
              </div>
              <div>
                <dt>Over 90 days</dt>
                <dd>{data.inventory.ageing.overThresholdHoldingCount}</dd>
              </div>
            </dl>
          </article>
        </section>
      </main>
    </DashboardShell>
  );
}

function PlaceholderPage({ path }: { path: string }) {
  const item = appNav.find((entry) => entry.href === path) ?? appNav[0]!;
  const Icon = item.icon;
  return (
    <DashboardShell activePath={path} user={null} demo>
      <main className="dashboard-content">
        <div className="placeholder-screen">
          <span className="icon-tile large">
            <Icon />
          </span>
          <p className="eyebrow">V1 route wired</p>
          <h1>{item.label}</h1>
          <p>
            The backend boundary for this area exists. Its focused visual
            workflow is the next frontend slice; the live overview already reads
            the dashboard API.
          </p>
          <div>
            <a className="button button-primary" href="/dashboard">
              Back to overview
            </a>
            <a className="button button-quiet" href="/product">
              View product map
            </a>
          </div>
        </div>
      </main>
    </DashboardShell>
  );
}

export default function App() {
  const path = useMemo(
    () => window.location.pathname.replace(/\/$/u, "") || "/",
    [],
  );
  useEffect(() => setPageMetadata(path), [path]);

  if (path === "/sign-in") return <AuthPage mode="sign-in" />;
  if (path === "/create-account") return <AuthPage mode="create-account" />;
  if (path === "/dashboard") return <DashboardPage />;
  if (appNav.some((entry) => entry.href === path))
    return <PlaceholderPage path={path} />;
  if (path === "/pricing") return <PricingPage />;
  if (path === "/security") return <SecurityPage />;
  if (path === "/product") return <MarketingPage productFocus />;
  return <MarketingPage />;
}
